import React from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface GuideCardProps {
  guide: {
    id: string;
    title: string;
    description: string;
    category: string;
    image: string;
    alt: string;
    readTime: string;
    difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
    rating: number;
    reviewCount: number;
    author: {
      name: string;
      credentials: string;
      avatar: string;
      avatarAlt: string;
    };
  };
}

const GuideCard = ({ guide }: GuideCardProps) => {
  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Beginner':
        return 'bg-success text-success-foreground';
      case 'Intermediate':
        return 'bg-warning text-warning-foreground';
      case 'Advanced':
        return 'bg-error text-error-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="bg-card rounded-xl shadow-soft hover:shadow-elevated transition-all duration-300 overflow-hidden group">
      <div className="relative h-48 overflow-hidden">
        <AppImage
          src={guide.image}
          alt={guide.alt}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-4 right-4">
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getDifficultyColor(guide.difficulty)}`}>
            {guide.difficulty}
          </span>
        </div>
      </div>

      <div className="p-6">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-primary bg-primary/10 px-3 py-1 rounded-full">
            {guide.category}
          </span>
          <div className="flex items-center space-x-1">
            <Icon name="ClockIcon" size={16} variant="outline" className="text-muted-foreground" />
            <span className="text-sm text-muted-foreground">{guide.readTime}</span>
          </div>
        </div>

        <h3 className="text-xl font-headline font-semibold text-foreground mb-2 line-clamp-2 group-hover:text-primary transition-colors">
          {guide.title}
        </h3>

        <p className="text-muted-foreground text-sm mb-4 line-clamp-3">
          {guide.description}
        </p>

        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
              <AppImage
                src={guide.author.avatar}
                alt={guide.author.avatarAlt}
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">{guide.author.name}</p>
              <p className="text-xs text-muted-foreground">{guide.author.credentials}</p>
            </div>
          </div>

          <div className="flex items-center space-x-1">
            <Icon name="StarIcon" size={16} variant="solid" className="text-warning" />
            <span className="text-sm font-semibold text-foreground">{guide.rating}</span>
            <span className="text-xs text-muted-foreground">({guide.reviewCount})</span>
          </div>
        </div>

        <Link
          href={`/care-guides-hub/${guide.id}`}
          className="mt-4 w-full flex items-center justify-center space-x-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium"
        >
          <span>Read Guide</span>
          <Icon name="ArrowRightIcon" size={16} variant="outline" />
        </Link>
      </div>
    </div>
  );
};

export default GuideCard;